// file: src/TowingDashboard/Sidebar.jsx
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, LayoutGroup } from "framer-motion";
import Chart from "../Icons/ChartIcon.jsx";
import BrandLogo from "../components/layout/BrandLogo.jsx";
import {
  fadeUp,
  staggerContainer,
  navPillSpring,
} from "../Animations/motionpresets.js";

const BLUE = "#1271EB";
const BLUE_LIGHT = "#4D95F0";

/* ---------- micro variants ---------- */
const pressy = {
  whileHover: { y: -1 },
  whileTap: { scale: 0.98 },
  transition: { type: "spring", stiffness: 420, damping: 28 },
};

/* ---------- shared classes ---------- */
const linkBase =
  "group relative flex items-center gap-3 rounded-xl px-3.5 py-3 text-[13px] ring-1 ring-inset transition-all";
const inactive =
  "text-slate-700 ring-slate-200/60 hover:text-[#0F63CE] hover:bg-[#4D95F0]/8 hover:ring-[#4D95F0]/30";
const activeBase =
  "text-slate-900 bg-white ring-white shadow-[0_14px_36px_rgba(18,113,235,.14)]";

/* ---------- utils ---------- */
function clsx(...p) {
  return p.filter(Boolean).join(" ");
}

/* Cloudinary helper (preserves variable pipeline like $w_..,$h_..) */
function resolveCloudinary(url, { w = 640, h = 380 } = {}) {
  if (!url) return "";
  return url.replace(/\$w_\d+/g, `$w_${w}`).replace(/\$h_\d+/g, `$h_${h}`);
}

/* ---------- subtle shimmering divider ---------- */
function SheenDivider() {
  return (
    <div className="relative h-px w-full overflow-hidden rounded-full bg-gradient-to-r from-transparent via-slate-200 to-transparent">
      <span
        className="absolute -left-1/4 top-0 h-px w-1/2"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(18,113,235,.25), transparent)",
          animation: "sheen 3s linear infinite",
        }}
      />
      <style>
        {`
          @keyframes sheen {
            0% { transform: translateX(0) }
            100% { transform: translateX(200%) }
          }
        `}
      </style>
    </div>
  );
}

/* ---------- starfield footer (twinkling blue stars) ---------- */
function StarfieldFooter({ className = "" }) {
  const stars = React.useMemo(
    () => [
      [8, 18, 0.8],
      [24, 36, 0.55],
      [72, 22, 0.7],
      [56, 64, 0.6],
      [86, 14, 0.75],
      [38, 78, 0.5],
      [12, 54, 0.65],
      [90, 48, 0.58],
      [66, 86, 0.62],
      [30, 12, 0.68],
      [48, 28, 0.57],
      [14, 86, 0.64],
    ],
    []
  );

  return (
    <div className={clsx("relative", className)}>
      <div
        className="relative h-32 w-full overflow-hidden rounded-2xl border border-[#BBD1F7]/70 bg-white"
        style={{
          backgroundImage: `
            radial-gradient(120% 80% at 10% 90%, rgba(77,149,240,.16), transparent 60%),
            radial-gradient(120% 80% at 90% 10%, rgba(18,113,235,.10), transparent 60%),
            linear-gradient(180deg, rgba(255,255,255,.96), rgba(255,255,255,.88))`,
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,.9), 0 20px 54px rgba(2,6,23,.06)",
        }}
        aria-hidden
      >
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            boxShadow:
              "inset 0 0 0 1px rgba(187,209,247,.65), inset 0 -18px 34px rgba(18,113,235,.12)",
          }}
        />
        <div
          className="pointer-events-none absolute -top-10 left-1/2 h-20 w-[120%] -translate-x-1/2 rounded-[999px] blur-[14px]"
          style={{
            background:
              "radial-gradient(60% 40% at 50% 100%, rgba(255,255,255,.65), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 100% at 0% 100%, rgba(18,113,235,.10), transparent 60%), radial-gradient(90% 80% at 100% 0%, rgba(77,149,240,.15), transparent 60%)",
          }}
        />
        <svg className="absolute inset-0 h-full w-full">
          {stars.map(([x, y, s], i) => (
            <g
              key={i}
              style={{
                transform: `translate(${x}%, ${y}%) scale(${s})`,
                transformOrigin: "center",
              }}
            >
              <circle
                r="6"
                fill="none"
                stroke={BLUE}
                strokeWidth="0.6"
                opacity="0.18"
                className="animate-[twinkle_3.4s_ease-in-out_infinite]"
                style={{ animationDelay: `${i * 180}ms` }}
              />
              <path
                d="M0 -2 L0.6 -0.6 L2 0 L0.6 0.6 L0 2 L-0.6 0.6 L-2 0 L-0.6 -0.6 Z"
                fill={BLUE}
                opacity="0.85"
                className="animate-[blink_3.4s_ease-in-out_infinite]"
                style={{ animationDelay: `${i * 180}ms` }}
              />
            </g>
          ))}
        </svg>
        <div className="absolute bottom-2 left-2 text-[11px] tracking-wider text-slate-600">
          2025 • Operational
        </div>
      </div>

      <style>
        {`
          @keyframes twinkle {
            0% { transform: scale(0.7); opacity: .12 }
            50% { transform: scale(1); opacity: .24 }
            100% { transform: scale(0.7); opacity: .12 }
          }
          @keyframes blink {
            0%, 100% { opacity: .65 }
            50% { opacity: 1 }
          }
        `}
      </style>
    </div>
  );
}

/* ---------- a single nav item ---------- */
function Item({ to, icon, label, onClick, end = false }) {
  return (
    <motion.div variants={fadeUp}>
      <NavLink
        to={to}
        end={end}
        onClick={onClick}
        className={({ isActive }) =>
          clsx(linkBase, isActive ? activeBase : inactive)
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <motion.span
                layoutId="navActiveStripe"
                className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full"
                style={{ background: BLUE }}
                transition={navPillSpring}
              />
            )}
            <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
              <span
                className="absolute inset-0 rounded-xl opacity-0 transition-opacity group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(120% 100% at 0% 0%, rgba(77,149,240,.08), transparent 45%)",
                }}
              />
            </span>
            <span className="relative z-10 flex w-full items-center gap-3">
              <motion.span
                className={clsx(
                  "inline-grid h-7 w-7 place-items-center rounded-full border text-[#1271EB]",
                  isActive
                    ? "bg-[#4D95F0]/15 border-[#4D95F0]/30"
                    : "bg-[#4D95F0]/10 border-[#4D95F0]/25 group-hover:bg-[#4D95F0]/20"
                )}
                {...pressy}
              >
                {icon}
              </motion.span>
              <motion.span
                className="truncate"
                whileHover={{ y: -0.5 }}
                transition={{ type: "spring", stiffness: 420, damping: 32 }}
              >
                {label}
              </motion.span>
              <span className="ml-auto inline-flex h-1.5 w-1.5 items-center justify-center">
                <span
                  className={clsx(
                    "h-1.5 w-1.5 rounded-full transition-all",
                    isActive ? "opacity-100" : "opacity-0"
                  )}
                  style={{ background: BLUE }}
                />
              </span>
            </span>
          </>
        )}
      </NavLink>
    </motion.div>
  );
}

/* ---------- Image viewer bits ---------- */
function ImageCard({ img, index, onOpen }) {
  const thumb = resolveCloudinary(img?.url, { w: 640, h: 380 });
  const title = `Image ${index + 1}${
    img?.width && img?.height ? ` • ${img.width}×${img.height}` : ""
  }`;
  return (
    <button
      onClick={onOpen}
      className="group relative aspect-[16/9] w-[min(86vw,360px)] sm:w-[min(48vw,360px)] lg:w-[min(30vw,340px)] xl:w-[min(26vw,320px)] 2xl:w-[300px] overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm ring-1 ring-white/70 transition hover:shadow-md"
      aria-label={title}
    >
      <img
        src={thumb}
        alt={img?.id || `Facility image ${index + 1}`}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover"
        draggable="false"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/0 to-black/0 opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="absolute bottom-1 left-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white">
        {index + 1}
      </div>
    </button>
  );
}

function Lightbox({ images, openIndex, onClose, onChange }) {
  const img = images?.[openIndex];

  React.useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        onChange(Math.min(images.length - 1, openIndex + 1));
      if (e.key === "ArrowLeft") onChange(Math.max(0, openIndex - 1));
    }
    if (img) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [img, images, openIndex, onClose, onChange]);

  if (!img) return null;
  const large = resolveCloudinary(img.url, { w: 1600, h: 1000 });

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative max-h-[90vh] w-full max-w-6xl"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={large}
          alt={img?.id || "Facility image"}
          className="max-h-[80vh] w-full rounded-2xl object-contain shadow-2xl"
        />
        <button
          onClick={onClose}
          className="absolute right-2 top-2 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur hover:bg-black/70"
        >
          Close
        </button>
        <div className="absolute inset-x-0 bottom-2 mx-auto flex w-full max-w-[260px] items-center justify-between gap-2">
          <button
            onClick={() => onChange(Math.max(0, openIndex - 1))}
            className="rounded-md bg-white/85 px-3 py-1 text-xs font-semibold text-slate-800 hover:bg-white"
          >
            Prev
          </button>
          <span className="rounded-md bg-white/85 px-3 py-1 text-[11px] font-semibold text-slate-700">
            {openIndex + 1} / {images.length}
          </span>
          <button
            onClick={() => onChange(Math.min(images.length - 1, openIndex + 1))}
            className="rounded-md bg-white/85 px-3 py-1 text-xs font-semibold text-slate-800 hover:bg-white"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- License Plate Preview (blue touch; plate searches only) ---------------- */
function PlatePreview({ plate }) {
  if (!plate) return null;

  return (
    <div className="mt-6 px-4">
      <div className="mb-2 flex items-center gap-2 px-1">
        <div
          className="h-1 w-1 rounded-full"
          style={{ background: BLUE, boxShadow: `0 0 0 3px ${BLUE}22` }}
        />
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
          License Plate
        </div>
      </div>

      {/* Card shell */}
      <div
        className="relative rounded-2xl bg-white/95 backdrop-blur p-3 shadow-[0_16px_40px_rgba(2,6,23,.06)] ring-1"
        style={{ boxShadow: "inset 0 1px 0 #fff", borderColor: "#C7DAFF" }}
        aria-label={`License plate ${plate}`}
      >
        {/* Plate body with blue rim */}
        <div
          className="relative mx-auto aspect-[16/9] w-full max-w-[520px] rounded-[18px]"
          style={{
            background:
              "linear-gradient(180deg,#F7FAFF 0%,#FFFFFF 60%,#F2F7FF 100%)",
            boxShadow:
              "inset 0 0 0 1px rgba(15,23,42,.06), inset 0 12px 24px rgba(18,113,235,.06)",
          }}
        >
          {/* thin blue outer glow */}
          <span
            className="pointer-events-none absolute inset-0 rounded-[18px]"
            style={{
              boxShadow: `0 0 0 2px ${BLUE}14, 0 8px 24px ${BLUE}12 inset`,
            }}
          />

          {/* header chip */}
          <div
            className="absolute inset-x-3 top-2 grid h-7 place-items-center rounded-lg text-[10px] font-semibold tracking-wider ring-1"
            style={{
              color: BLUE,
              background: "linear-gradient(180deg,#FFFFFF 0%,#F6FAFF 100%)",
              boxShadow: "inset 0 1px 0 #fff",
              borderColor: "#D7E6FF",
            }}
          >
            Plate
          </div>

          {/* bolt caps */}
          {[
            ["left-2 top-2"],
            ["right-2 top-2"],
            ["left-2 bottom-2"],
            ["right-2 bottom-2"],
          ].map((pos, i) => (
            <div
              key={i}
              className={`absolute ${pos} h-3 w-3 rounded-full bg-slate-200 ring-2 ring-slate-300`}
            />
          ))}

          {/* center blue band */}
          <div
            className="absolute inset-x-4 top-1/2 -translate-y-1/2 h-16 rounded-lg"
            style={{
              background:
                "linear-gradient(90deg, rgba(18,113,235,.08), rgba(77,149,240,.14), rgba(18,113,235,.08))",
              boxShadow:
                "inset 0 0 0 1px rgba(18,113,235,.12), 0 12px 24px rgba(2,6,23,.05)",
            }}
          />

          {/* plate text */}
          <div className="absolute inset-0 grid place-items-center px-4">
            <div
              className="font-mono select-all text-[clamp(26px,6.6vw,44px)] tracking-[0.18em] sm:tracking-[0.22em] text-slate-900"
              style={{ textShadow: "0 1px 0 #fff", letterSpacing: "0.14em" }}
            >
              {plate}
            </div>
          </div>

          {/* footer chips */}
        </div>
      </div>
    </div>
  );
}

/* Horizontal scroller + loader (for Title searches) */
function ImageScroller({ facilityImages, loading }) {
  const images = Array.isArray(facilityImages?.images)
    ? facilityImages.images
    : [];
  const count = facilityImages?.image_count ?? images.length;

  const [openIndex, setOpenIndex] = React.useState(-1);
  const ref = React.useRef(null);

  function scrollByDir(dir = 1) {
    const el = ref.current;
    if (!el) return;
    const delta = el.clientWidth * 0.9 * dir;
    el.scrollBy({ left: delta, behavior: "smooth" });
  }

  // Nothing to show at all
  if (!loading && images.length === 0) return null;

  return (
    <div className="mt-6 px-4">
      <div className="mb-2 flex items-center gap-2 px-1">
        <div
          className="h-1 w-1 rounded-full"
          style={{ background: BLUE, boxShadow: `0 0 0 3px ${BLUE}22` }}
        />
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Facility Photos
        </div>
        <div className="ml-auto text-[11px] text-slate-500">
          {loading ? "Loading…" : `${count} images`}
        </div>
      </div>

      <div className="relative">
        {/* soft edge glows */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent rounded-l-2xl z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent rounded-r-2xl z-10" />

        {/* controls */}
        <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-20 flex items-center justify-between">
          <button
            onClick={() => scrollByDir(-1)}
            className="pointer-events-auto ml-1 rounded-full bg-white/95 p-2 shadow ring-1 ring-slate-200 hover:bg-white"
            aria-label="Scroll left"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M14 6l-6 6 6 6" />
            </svg>
          </button>
          <button
            onClick={() => scrollByDir(1)}
            className="pointer-events-auto mr-1 rounded-full bg-white/95 p-2 shadow ring-1 ring-slate-200 hover:bg-white"
            aria-label="Scroll right"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M10 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        {/* scroll strip (centered, first item clickable) */}
        <div
          ref={ref}
          className="
    no-scrollbar overflow-x-auto
    rounded-2xl border border-slate-200 bg-white/95 backdrop-blur
    px-6 md:px-8 py-3
  "
          onWheel={(e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
              e.currentTarget.scrollLeft += e.deltaY;
            }
          }}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") scrollByDir(1);
            if (e.key === "ArrowLeft") scrollByDir(-1);
          }}
          aria-label="Facility images"
        >
          {/* inner row centers itself when content is narrow */}
          <div
            className="min-w-max mx-auto grid auto-cols-max grid-flow-col gap-3"
            style={{ scrollSnapType: "x mandatory" }}
          >
            {loading && images.length === 0
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={`sk-${i}`}
                    className="aspect-[16/9] w-[min(86vw,360px)] sm:w-[min(48vw,360px)] lg:w-[min(30vw,340px)] xl:w-[min(26vw,320px)] 2xl:w-[300px] rounded-2xl bg-slate-100 animate-pulse snap-center"
                    style={{ scrollSnapAlign: "center" }}
                  />
                ))
              : images.map((img, i) => (
                  <div
                    key={img.id || i}
                    className="snap-center"
                    style={{ scrollSnapAlign: "center" }}
                  >
                    <ImageCard
                      img={img}
                      index={i}
                      onOpen={() => setOpenIndex(i)}
                    />
                  </div>
                ))}
          </div>
        </div>
      </div>

      <Lightbox
        images={images}
        openIndex={openIndex}
        onClose={() => setOpenIndex(-1)}
        onChange={setOpenIndex}
      />
    </div>
  );
}

/* ---------- component ---------- */
export default function Sidebar({
  // Wider but tasteful on all breakpoints
  className = "w-[90vw] sm:w-[320px] md:w-[360px] lg:w-[380px] xl:w-[400px] 2xl:w-[420px] h-[calc(100vh-20px)]",
  onNavigate,
}) {
  const navigate = useNavigate();

  // images + loading are driven by global events fired from Overview
  const [facilityImages, setFacilityImages] = React.useState(null);
  const [imagesLoading, setImagesLoading] = React.useState(false);

  // license plate preview text
  const [plateText, setPlateText] = React.useState("");

  React.useEffect(() => {
    const onImages = (e) => setFacilityImages(e?.detail || null);
    const onLoading = (e) => setImagesLoading(!!e?.detail);
    const onPlate = (e) => setPlateText(String(e?.detail || "").toUpperCase());

    window.addEventListener("towing:facility-images", onImages);
    window.addEventListener("towing:sidebar:images-loading", onLoading);
    window.addEventListener("towing:sidebar:plate", onPlate);

    return () => {
      window.removeEventListener("towing:facility-images", onImages);
      window.removeEventListener("towing:sidebar:images-loading", onLoading);
      window.removeEventListener("towing:sidebar:plate", onPlate);
    };
  }, []);

  return (
    <aside
      className={clsx(
        "sticky top-2 rounded-3xl",
        "shrink-0 relative overflow-hidden flex flex-col",
        "border border-slate-200 bg-white/80 backdrop-blur-xl",
        "shadow-[0_24px_60px_rgba(2,6,23,.06)]",
        "before:pointer-events-none before:absolute before:inset-0 before:rounded-3xl before:shadow-[inset_0_1px_0_rgba(255,255,255,.9),inset_0_0_0_1px_rgba(15,23,42,.03)]",
        "after:pointer-events-none after:absolute after:inset-0 after:rounded-3xl",
        "after:[box-shadow:inset_0_0_0_1px_rgba(18,113,235,.05)]",
        className
      )}
    >
      {/* soft radial glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -left-24 h-56 w-56 rounded-full blur-2xl opacity-40"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 50%, rgba(18,113,235,.18), transparent)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-20 -right-16 h-56 w-56 rounded-full blur-2xl opacity-30"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 50%, rgba(77,149,240,.16), transparent)",
        }}
      />

      {/* vertical glow rail */}
      <div
        className="pointer-events-none absolute right-0 inset-y-0 w-px"
        style={{
          background: `linear-gradient(180deg, rgba(15,23,42,.08), ${BLUE}33, rgba(15,23,42,.08))`,
        }}
      />

      {/* brand card */}
      <div className="px-4 pt-4">
        <motion.div
          {...pressy}
          className="relative flex items-center gap-3 rounded-2xl border border-white/70 bg-white/92 px-4 py-3"
          style={{
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,.85), 0 22px 46px rgba(18,113,235,.12)",
          }}
        >
          <div className="shrink-0">
            <BrandLogo size={42} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold leading-5 tracking-tight text-slate-900">
              Towing Portal
            </div>
            <div className="text-xs leading-4 text-slate-500">
              Operations Console
            </div>
          </div>
          <div className="ml-auto shrink-0">
            <div className="relative inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/95 px-2 py-1 text-[10px] font-semibold tracking-wide text-slate-600">
              <span
                className="relative inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: BLUE }}
              >
                <span
                  className="absolute inset-[-4px] rounded-full"
                  style={{
                    boxShadow: `0 0 0 0 ${BLUE}33`,
                    animation: "pulseGlow 2s ease-out infinite",
                  }}
                />
              </span>
              LIVE
            </div>
          </div>
          <div
            className="pointer-events-none absolute inset-0 rounded-2xl"
            style={{
              boxShadow:
                "0 0 0 1px rgba(255,255,255,.75) inset, 0 -10px 22px rgba(77,149,240,.10) inset",
            }}
          />
          <style>
            {`
              @keyframes pulseGlow {
                0% { box-shadow: 0 0 0 0 ${BLUE}33 }
                70% { box-shadow: 0 0 0 8px transparent }
                100% { box-shadow: 0 0 0 0 transparent }
              }
            `}
          </style>
        </motion.div>
      </div>

      {/* nav */}
      <div className="mt-6 px-4">
        <div className="flex items-center gap-2 px-1">
          <div
            className="h-1 w-1 rounded-full"
            style={{ background: BLUE, boxShadow: `0 0 0 3px ${BLUE}22` }}
          />
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Navigation
          </div>
          <div className="ml-auto flex-1">
            <SheenDivider />
          </div>
        </div>

        <LayoutGroup id="sidebar-nav">
          <motion.nav
            variants={staggerContainer(0.08, 0.06)}
            initial="hidden"
            animate="show"
            className="mt-3 grid gap-2.5"
          >
            <Item
              to="/towing/dashboard"
              end
              label="Dashboard"
              icon={<Chart className="h-5 w-5" />}
              onClick={onNavigate}
            />
          </motion.nav>
        </LayoutGroup>
      </div>

      {/* Plate preview (shows only when a plate value is provided) */}
      <PlatePreview plate={plateText} />

      {/* Title photos (Title search controls this via events) */}
      <ImageScroller facilityImages={facilityImages} loading={imagesLoading} />

      {/* push footer to bottom */}
      <div className="flex-1" />

      {/* footer */}
      <div className="px-4 pb-5 pt-6">
        <StarfieldFooter />
      </div>

      {/* hide native scrollbar cross-browser */}
      <style>
        {`
          .no-scrollbar { scrollbar-width: none; }
          .no-scrollbar::-webkit-scrollbar { display: none; }
        `}
      </style>
    </aside>
  );
}
